import { useContext, useEffect, useState } from 'react'

import { AppStateContext } from '../../../state/AppProvider'

import { InputAdornment, TextField } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { first, isEmpty } from 'lodash'
import { ChangeSourceEnum, FormField, updateExperimentFormField } from '../../../api'
import { ToastMessage } from '../../ExperimentForm/ExperimentFormToast'
import { FormFieldLabel } from '../FormFieldLabel'
import styles from './TextFormField.module.css'

interface Props {
  name: string
  stepName: string
  fieldName: string
  convId: string
  data: FormField<string>
  gridSize: number
  disabled?: boolean
  multiline?: boolean
  rows?: number
  tooltipInfo?: React.ReactNode
  validIcon?: React.ReactNode
  onError?: (message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => void
}

export const TextFormField = ({
  name,
  stepName,
  fieldName,
  convId,
  data,
  gridSize,
  disabled = false,
  multiline = false,
  rows = 1,
  tooltipInfo,
  validIcon,
  onError,
}: Props) => {
  const appStateContext = useContext(AppStateContext)
  const [value, setValue] = useState(data?.field_value ?? '')

  async function handleChange() {
    if ((data?.field_value ?? '') === value) return

    try {
      const newConversation = await updateExperimentFormField(
        stepName,
        fieldName,
        value,
        convId,
        appStateContext?.state.shouldValidateForm ?? false,
        appStateContext?.state.frontendSettings || undefined,
      )

      if (newConversation) {
        const messages = appStateContext?.state.currentChat?.messages || []
        newConversation.messages = messages

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: newConversation })
      }
    } catch (error) {
      const errorWithReactElement = error as Error & { reactElement?: React.ReactNode }
      onError?.({
        title: 'Error',
        description:
          errorWithReactElement?.reactElement ||
          (error instanceof Error ? error.message : 'An error occurred while updating the field'),
        type: 'error',
      })
    }
  }

  function canShowValidIcon() {
    return validIcon && !isEmpty(data?.field_value) && isEmpty(data.errors) && !data?.disabled
  }

  useEffect(() => {
    setValue(data?.field_value ?? '')
  }, [data?.field_value])

  if (data?.hidden === true) {
    return <></>
  }

  return (
    <Grid
      size={gridSize}
      id={`field_${fieldName}`}>
      <FormFieldLabel
        text={name}
        isAiGenerated={data?.change_source !== ChangeSourceEnum.MANUAL}
        tooltipInfo={tooltipInfo}
      />

      <TextField
        error={!isEmpty(data.errors)}
        helperText={first(data.errors)}
        label={isEmpty(value) && `Enter ${name}`}
        className={styles.textField}
        onBlur={handleChange}
        disabled={disabled}
        value={value}
        onChange={e => {
          setValue(e.target.value)
        }}
        fullWidth
        multiline={multiline}
        rows={rows}
        margin="normal"
        variant="outlined"
        slotProps={{
          inputLabel: { shrink: false },
          input: {
            classes: { disabled: styles.disabledTextField, error: styles.errorInput },
            endAdornment: canShowValidIcon() && <InputAdornment position="end">{validIcon}</InputAdornment>,
          },
        }}
      />
    </Grid>
  )
}
